<!--
  OneOffItems.vue
  콜봇 월 정산 · 정산 탭의 "일회성 항목" 목록(화면 명세 2-4 마지막 항목, 요청 계약 4-4).

  사용량과 상관없는 청구(예: 상담사 전화기 VVX150 1대 127,000원, 부가세 별도)를 이 달 정산에 넣는다.
  - 카드 머리: '일회성 항목 n건' · 합계 · 한 줄 설명 · [항목 추가]. 표(이름 / 금액 / 메모 / 넣은 사람·시각 / 수정·삭제)는 항목이
    있을 때만. `[항목 추가]` → OneOffItemModal.
  - 추정 중·검토 필요·마감 실패인 달에서만 추가·수정·삭제한다. 확정·마감 중인 달은 버튼을 끄고 "재수정 뒤 추가"
    툴팁을 단다(서버도 409 STATEMENT_LOCKED 로 막는다 — 받으면 안내 후 상세를 다시 불러온다).
  - 요금표 v70 이전에 만든 정산서(`statement.legacy`, ~/md/front/admin-콜봇-월정산-call-api-구현회신.md "2026-10-16 개정"
    8)는 일회성 항목 쓰기가 409 STATEMENT_LEGACY_LINES 다 — 버튼을 끄고 "[다시 계산] 뒤"로 안내한다(받으면 안내 후
    다시 불러온다).
  - 검토 필요인 달은 쓰기마다 먼저 확인창으로 "정산에 바로 반영되고 자동 마감이 다시 계산하지 않음"을 알린다.
  - 내부·테스트 회사는 청구가 0원이라 금액에 넣지 않는다(기록만 남는다) — 안내만 하고 쓰기는 막지 않는다.
  - 경고를 만들지 않고 자동 마감을 막지 않는다. 마진 계산에서 뺀다.
  - 정산 시작일 전 달(notBillableView)은 [항목 추가]·[수정]을 숨기고 [삭제]만 남긴다(서버도 만들기·수정은 409
    SETTLEMENT_NOT_STARTED). 쓰기가 그 409 를 받으면(화면이 낡음) 셸의 inject('settlementOnNotStarted') 를 불러 안내 후
    다시 받는다.

  데이터: 마운트할 때 한 번 `api.listOneOffItems`(넣은 사람·시각이 여기만 있다). 쓰기 뒤에는 이 목록과
  정산 상세(`reload()` — 헤더·표·마감 패널의 금액)를 함께 다시 부른다. 정산 탭은 회사·달마다 한 번 마운트되므로
  탭을 오갈 때 다시 부르지 않는다.
  요청: ~/md/front/admin-콜봇-월정산-프론트요청.md "화면 명세 2-4", "요청 계약 4-4"
-->
<template>
  <section class="oneoff" :aria-labelledby="titleId">
    <div class="oneoff__bar">
      <div class="oneoff__head">
        <h2 :id="titleId" class="oneoff__title">
          일회성 항목
          <span v-if="loaded" class="oneoff__count">{{ items.length }}건</span>
          <span v-if="items.length" class="oneoff__sum">합계 {{ formatWon(listAmount) }}</span>
        </h2>
        <a-tooltip title="공급가액(부가세 별도) · 경고를 만들지 않고 자동 마감을 막지 않습니다">
          <span class="oneoff__desc">단말기처럼 사용량과 상관없는 청구 · 마진 계산에서 뺍니다</span>
        </a-tooltip>
      </div>
      <button
        type="button"
        class="oneoff__mini"
        title="새로고침"
        aria-label="일회성 항목 목록 새로고침"
        :disabled="loading"
        @click="load"
      >
        <ReloadOutlined :spin="loading && loaded" />
      </button>
      <a-tooltip v-if="!hideAdd" :title="addDisabledReason || undefined">
        <span class="oneoff__add-wrap">
          <a-button :disabled="!!addDisabledReason" @click="openCreate">
            <PlusOutlined />
            항목 추가
          </a-button>
        </span>
      </a-tooltip>
    </div>

    <p v-if="internal" class="oneoff__note" role="status">{{ ONE_OFF_INTERNAL_NOTE }}</p>
    <p v-if="notBillableText" class="oneoff__locked">{{ notBillableText }}</p>

    <div v-if="loadError && !loaded" class="oneoff__state is-error" role="alert">
      <span>{{ loadError }}</span>
      <a-button size="small" :loading="loading" @click="load">다시 시도</a-button>
    </div>
    <a-skeleton v-else-if="!loaded" active :title="false" :paragraph="{ rows: 2 }" />
    <template v-else>
      <div v-if="loadError" class="oneoff__banner" role="alert">
        <span>{{ loadError }}</span>
        <button type="button" class="oneoff__link" :disabled="loading" @click="load">다시 시도</button>
      </div>

      <p v-if="!items.length && lockedText && !notBillableText" class="oneoff__locked">{{ lockedText }}</p>

      <div v-if="items.length" class="oneoff__scroll">
        <table class="oneoff__table">
          <caption class="oneoff__sr">이 달 일회성 항목</caption>
          <thead>
            <tr>
              <th scope="col">이름</th>
              <th scope="col" class="is-num">금액(공급가액)</th>
              <th scope="col">메모</th>
              <th scope="col">넣은 사람·시각</th>
              <th scope="col" class="is-actions"><span class="oneoff__sr">관리</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="it in items" :key="it.item_id">
              <th scope="row" class="oneoff__name">{{ it.name || EMPTY }}</th>
              <td class="oneoff__amount">{{ formatWon(it.amount) }}</td>
              <td class="oneoff__memo">{{ it.memo || EMPTY }}</td>
              <td class="oneoff__who">
                <span>{{ actorLabel(it.created_by, it.created_by_name) }} · {{ formatKstDateTime(it.created_at) }}</span>
                <span v-if="wasUpdated(it)" class="oneoff__updated">
                  수정 {{ actorLabel(it.updated_by, it.updated_by_name) }} · {{ formatKstDateTime(it.updated_at) }}
                </span>
              </td>
              <td class="oneoff__actions">
                <a-tooltip v-if="!hideEdit" :title="rowDisabledReason('수정') || undefined">
                  <span>
                    <a-button size="small" :disabled="!!rowDisabledReason('수정')" @click="openEdit(it)">
                      <EditOutlined />
                      수정
                    </a-button>
                  </span>
                </a-tooltip>
                <a-tooltip :title="rowDisabledReason('삭제') || undefined">
                  <span>
                    <a-button
                      size="small"
                      danger
                      :disabled="!!rowDisabledReason('삭제')"
                      :loading="deletingId === it.item_id"
                      @click="confirmDelete(it)"
                    >
                      <DeleteOutlined />
                      삭제
                    </a-button>
                  </span>
                </a-tooltip>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <OneOffItemModal
      :open="modalOpen"
      :item="editing"
      :submitting="submitting"
      :month="month"
      :internal="internal"
      @close="closeModal"
      @submit="onSubmit"
    />
  </section>
</template>

<script setup>
import { computed, getCurrentInstance, h, inject, onMounted, ref } from 'vue';
import { Modal, message } from 'ant-design-vue';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons-vue';

import OneOffItemModal from './OneOffItemModal.vue';
import { EMPTY, formatKstDateTime, formatWon, isNumber } from '../../service/settlementFormat';
import {
  ONE_OFF_INTERNAL_NOTE,
  ONE_OFF_LOCKED_TOOLTIP,
  ONE_OFF_REVIEW_CONFIRM_TEXT,
  STATEMENT_CONFLICT_TEXT,
  STATEMENT_LEGACY_LINES_TEXT,
  actorLabel,
  canEditOneOff,
  isInternalDetail,
  isLegacyStatementDetail,
  statementStatusOf,
} from '../../service/settlementRules';
import { isNotStartedError, notBillableView } from '../../service/settlementStart';
import {
  getCallApiErrorCode,
  isCallApiUnimplemented,
  parseCallApiError,
} from '@/views/Callbot/service/callApiError';

const LOCKED_TEXT = '이 달 정산이 확정됐거나 마감 중이라 일회성 항목을 바꿀 수 없습니다. 재수정한 뒤 바꾸세요.';

const props = defineProps({
  companyId: { type: String, required: true },
  month: { type: String, required: true },
  /** 정산 상세(마감 상태·구분) — 정산 탭은 셸이 첫 상세를 받은 뒤에만 마운트된다 */
  detail: { type: Object, required: true },
  api: { type: Object, required: true },
  /** 정산 상세를 다시 부른다 — 쓰기 뒤마다 */
  reload: { type: Function, required: true },
});

const titleId = `st-oneoff-${getCurrentInstance()?.uid ?? 0}`;

/** 셸(SettlementDetail)의 409 SETTLEMENT_NOT_STARTED 처리 — 안내 후 상세를 다시 받는다. 셸 밖이면 null */
const onNotStarted = inject('settlementOnNotStarted', null);

const items = ref([]);
const listAmount = ref(0);
const loaded = ref(false);
const loading = ref(false);
const loadError = ref('');
const unimplemented = ref(false);

const modalOpen = ref(false);
const editing = ref(null);
const submitting = ref(false);
const deletingId = ref(null);

const status = computed(() => statementStatusOf(props.detail));
const internal = computed(() => isInternalDetail(props.detail));
const inReview = computed(() => status.value === 'review');
/** v70 이전 정산서 — 다시 계산(확정이면 재수정 먼저)해야 일회성 항목을 쓸 수 있다(개정 8) */
const legacy = computed(() => isLegacyStatementDetail(props.detail));
const LEGACY_TOOLTIP = '[다시 계산] 뒤 추가';
const editable = computed(() => canEditOneOff(status.value) && !unimplemented.value && !legacy.value);

/** 정산 시작일 전 달 — 추가·수정은 숨기고 삭제만 남긴다 */
const notBillable = computed(() => notBillableView(props.detail));
const hideAdd = computed(() => notBillable.value.notBillable && notBillable.value.hide.oneOffAdd);
const hideEdit = computed(() => notBillable.value.notBillable && notBillable.value.hide.oneOffEdit);
const notBillableText = computed(() => {
  if (!notBillable.value.notBillable) return '';
  return items.value.length
    ? '정산 시작일 전 달이라 일회성 항목을 더하거나 고칠 수 없습니다. 남은 항목은 지울 수 있습니다.'
    : '정산 시작일 전 달이라 일회성 항목을 넣지 않습니다.';
});

/** 확정·마감 중인 달의 안내(빈 목록 아래) */
const lockedText = computed(() => {
  if (legacy.value) {
    return status.value === 'confirmed'
      ? '요금표 v70 이전 정산서라 [재수정] → [다시 계산] 뒤 바꿀 수 있습니다.'
      : '요금표 v70 이전 정산서라 [다시 계산] 뒤 바꿀 수 있습니다.';
  }
  if (status.value === 'confirmed') return '확정된 달이라 재수정 뒤 추가할 수 있습니다.';
  if (status.value === 'closing') return '자동 마감이 계산하는 중이라 지금은 바꿀 수 없습니다.';
  return '';
});

const addDisabledReason = computed(() => {
  if (unimplemented.value) return '일회성 항목 API 가 아직 준비되지 않았습니다.';
  if (legacy.value && canEditOneOff(status.value)) return LEGACY_TOOLTIP;
  return editable.value ? '' : ONE_OFF_LOCKED_TOOLTIP;
});

/** @param {'수정'|'삭제'} verb */
function rowDisabledReason(verb) {
  if (editable.value) return '';
  if (unimplemented.value) return addDisabledReason.value;
  if (legacy.value && canEditOneOff(status.value)) return `[다시 계산] 뒤 ${verb}`;
  return `재수정 뒤 ${verb}`;
}

function wasUpdated(it) {
  return isNumber(it?.updated_at) && isNumber(it?.created_at) && it.updated_at > it.created_at;
}

// 늦게 온 응답이 새 응답을 덮지 않게 마지막 요청만 반영한다
let loadSeq = 0;

async function load() {
  const seq = ++loadSeq;
  loading.value = true;
  try {
    const res = await props.api.listOneOffItems(props.companyId, props.month);
    if (seq !== loadSeq) return;
    items.value = Array.isArray(res?.items) ? res.items : [];
    listAmount.value = isNumber(res?.amount)
      ? res.amount
      : items.value.reduce((acc, it) => acc + (isNumber(it?.amount) ? it.amount : 0), 0);
    loadError.value = '';
    unimplemented.value = false;
    loaded.value = true;
  } catch (err) {
    if (seq !== loadSeq) return;
    unimplemented.value = isCallApiUnimplemented(err);
    loadError.value = parseCallApiError(err, '일회성 항목을 불러오지 못했습니다.', {
      unavailableMessage: '일회성 항목 API 가 아직 준비되지 않았습니다(call-api 미배포).',
    });
  } finally {
    if (seq === loadSeq) loading.value = false;
  }
}

onMounted(load);

/** 쓰기 뒤 — 이 목록과 정산 상세를 함께 */
async function refreshAfterWrite() {
  await Promise.all([load(), props.reload()]);
}

function openCreate() {
  if (hideAdd.value || addDisabledReason.value) return;
  editing.value = null;
  modalOpen.value = true;
}

function openEdit(it) {
  if (hideEdit.value || !editable.value) return;
  editing.value = it;
  modalOpen.value = true;
}

function closeModal() {
  if (submitting.value) return;
  modalOpen.value = false;
}

/**
 * 쓰기 실패 — 409 STATEMENT_LOCKED(그 사이 확정·마감 중), 404 ONE_OFF_ITEM_NOT_FOUND 는 안내 후 다시 부른다.
 * 409 STATEMENT_CONFLICT(그 사이 자동 마감·다른 사람이 정산을 바꿈 — 회신 표 10)는 안내 후 다시 부르되 모달은 연 채로
 * 둔다(입력을 두고, 바뀐 정산을 보고 다시 누른다).
 * @returns {Promise<boolean>} 모달을 닫아야 하면 true
 */
async function handleWriteError(err, fallback) {
  if (isNotStartedError(err)) {
    // 정산 시작일 전 달이 됐다(화면이 낡음) — 셸이 알리고 상세를 다시 받는다. 목록도 다시
    if (onNotStarted) onNotStarted();
    else await props.reload();
    void load();
    return true;
  }
  const code = getCallApiErrorCode(err);
  if (code === 'STATEMENT_LOCKED') {
    message.warning(LOCKED_TEXT);
    await refreshAfterWrite();
    return true;
  }
  if (code === 'STATEMENT_LEGACY_LINES') {
    message.warning(STATEMENT_LEGACY_LINES_TEXT);
    await refreshAfterWrite();
    return true;
  }
  if (code === 'STATEMENT_CONFLICT') {
    message.warning(STATEMENT_CONFLICT_TEXT);
    await refreshAfterWrite();
    return false;
  }
  if (code === 'ONE_OFF_ITEM_NOT_FOUND') {
    message.warning('이미 삭제된 항목입니다. 목록을 새로 불러옵니다.');
    await refreshAfterWrite();
    return true;
  }
  message.error(parseCallApiError(err, fallback));
  return false;
}

/** @param {{ name: string, amount: number, memo: string }} payload */
async function save(payload) {
  const target = editing.value;
  submitting.value = true;
  try {
    if (target) {
      await props.api.updateOneOffItem(props.companyId, target.item_id, payload);
      message.success(`일회성 항목을 수정했습니다: ${payload.name} ${formatWon(payload.amount)}`);
    } else {
      await props.api.createOneOffItem(props.companyId, { month: props.month, ...payload });
      message.success(`일회성 항목을 추가했습니다: ${payload.name} ${formatWon(payload.amount)}`);
    }
    modalOpen.value = false;
    await refreshAfterWrite();
  } catch (err) {
    const close = await handleWriteError(err, target ? '일회성 항목을 수정하지 못했습니다.' : '일회성 항목을 추가하지 못했습니다.');
    if (close) modalOpen.value = false;
  } finally {
    submitting.value = false;
  }
}

function onSubmit(payload) {
  if (submitting.value) return;
  if (!inReview.value) {
    save(payload);
    return;
  }
  const editingNow = !!editing.value;
  Modal.confirm({
    title: editingNow ? '일회성 항목 수정' : '일회성 항목 추가',
    content: ONE_OFF_REVIEW_CONFIRM_TEXT,
    okText: editingNow ? '수정' : '추가',
    cancelText: '취소',
    // save 는 reject 하지 않는다 — 끝날 때까지 확인 버튼이 로딩으로 남는다
    onOk: () => save(payload),
  });
}

async function remove(it) {
  deletingId.value = it.item_id;
  try {
    await props.api.deleteOneOffItem(props.companyId, it.item_id);
    message.success(`일회성 항목을 삭제했습니다: ${it.name}`);
    await refreshAfterWrite();
  } catch (err) {
    await handleWriteError(err, '일회성 항목을 삭제하지 못했습니다.');
  } finally {
    deletingId.value = null;
  }
}

function confirmDelete(it) {
  if (!editable.value || deletingId.value) return;
  const lead = `${it.name || '이 항목'}(${formatWon(it.amount)})을 이 달 정산에서 삭제합니다.`;
  Modal.confirm({
    title: '일회성 항목 삭제',
    content: inReview.value ? h('div', [h('p', lead), h('p', ONE_OFF_REVIEW_CONFIRM_TEXT)]) : lead,
    okText: '삭제',
    okType: 'danger',
    cancelText: '취소',
    // 되돌릴 수 없는 조작이라 Enter 한 번에 실행되지 않게 취소에 초점을 둔다
    autoFocusButton: 'cancel',
    onOk: () => remove(it),
  });
}
</script>

<style scoped lang="scss">
@use '../../styles/settlement' as *;

.oneoff {
  @include st-card;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 20px;
  color: $st-text;

  &__head {
    display: flex;
    flex: 1 1 320px;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }

  &__title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
  }

  &__count {
    margin-left: 4px;
    font-weight: 400;
    color: $st-muted;
  }

  &__locked {
    margin: 0;
    font-size: 12.5px;
    color: $st-muted;
  }

  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
  }

  &__desc {
    align-self: flex-start;
    font-size: 13px;
    color: $st-muted;
    cursor: help;
  }

  &__sum {
    @include st-num;
    margin-left: 8px;
    font-size: 14px;
    font-weight: 700;
  }

  &__mini {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: 1px solid $st-border-strong;
    border-radius: 6px;
    background: #fff;
    color: $st-text-secondary;
    cursor: pointer;

    &:disabled {
      cursor: default;
      opacity: 0.6;
    }
  }

  // 비활성 버튼도 툴팁이 뜨게 감싼다
  &__add-wrap {
    display: inline-block;
  }

  &__note {
    margin: 0;
    padding: 8px 12px;
    border: 1px solid $st-border;
    border-radius: 8px;
    background: $st-bg;
    font-size: 12.5px;
    color: $st-text-secondary;

    &.is-warn {
      border-color: $st-warning-border;
      background: $st-warning-bg;
      color: $st-warning;
    }
  }

  &__banner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 12px;
    padding: 8px 12px;
    border: 1px solid $st-danger-border;
    border-radius: 8px;
    background: $st-danger-bg;
    font-size: 12.5px;
    color: $st-danger;
  }

  &__link {
    height: auto; // 전역 button { height: 40px } 덮어쓰기
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    font-weight: 600;
    color: $st-accent;
    cursor: pointer;
  }

  &__state {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 18px 16px;
    border: 1px dashed $st-border-strong;
    border-radius: 10px;
    background: $st-bg;
    font-size: 12.5px;
    color: $st-text-secondary;

    &.is-error {
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px 12px;
      border-color: $st-danger-border;
      background: $st-danger-bg;
      color: $st-danger;
    }
  }

  // position: relative — 화면 낭독기용 숨김 글자(absolute)가 이 상자 밖으로 나가 페이지를 넓히지 않게
  &__scroll {
    position: relative;
    overflow-x: auto;
    border: 1px solid $st-border;
    border-radius: 10px;
  }

  &__table {
    width: 100%;
    min-width: 720px;
    border-collapse: collapse;
    font-size: 12.5px;

    th,
    td {
      padding: 8px 10px;
      // !important — CertiAccManage.vue 의 전역 td { border-bottom: transparent !important } 가 td 줄을 지워 항목 칸(th)에만 줄이 남는다
      border-bottom: 1px solid $st-border !important;
      text-align: left;
      vertical-align: top;
    }

    thead th {
      background: $st-bg;
      font-size: 12px;
      font-weight: 600;
      color: $st-text-secondary;
      white-space: nowrap;
    }

    tbody tr:last-child th,
    tbody tr:last-child td {
      border-bottom: none !important;
    }

    // 표 기본 정렬(왼쪽)보다 앞서야 해서 표 안에서 다시 적는다
    .is-num,
    .oneoff__amount {
      text-align: right;
    }

    .is-actions {
      width: 1%;
    }
  }

  &__sr {
    @include st-sr-only;
  }

  &__name {
    font-weight: 600;
  }

  &__amount {
    @include st-num;
    text-align: right;
    font-weight: 600;
  }

  &__memo {
    color: $st-text-secondary;
    word-break: break-word;
  }

  &__who {
    display: flex;
    flex-direction: column;
    gap: 2px;
    color: $st-text-secondary;
    white-space: nowrap;
  }

  &__updated {
    font-size: 11.5px;
    color: $st-muted;
  }

  &__actions {
    white-space: nowrap;

    > span + span {
      margin-left: 6px;
    }

    // 비활성 버튼도 툴팁이 뜨게 감싼 span
    > span {
      display: inline-block;
    }
  }
}
</style>
